import React from 'react';
import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { CheckSquareOutlined } from '@ant-design/icons';
import classes from './SuccessfulPayment.module.scss';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { useNavigate, useSearchParams } from 'react-router';
import { AxiosError } from 'axios';
import { updateRooms } from 'store/reducers/userDataCreator';
import billing from 'api/billing';
import { getTheme } from 'helpers/getTheme';

type SuccessfulPaymentPops = {
  isOpen: boolean;
  onCancel: () => void;
};

const SuccessfulPayment = ({ isOpen, onCancel }: SuccessfulPaymentPops) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isCreatingRoom, setIsCreatingRoom] = React.useState(false);
  const hasAttemptedCreationRef = React.useRef(false);
  const [searchParams] = useSearchParams();
  
  const { userData, userAndRoomStatus } = useAppSelector((store) => store.userData);
  const { messageApi } = useAppSelector((store) => store.windowState);

  const quoteId = searchParams.get('quote_id');

  const pendingRoomData = React.useMemo(() => {
    if (!quoteId) return null;
    const data = localStorage.getItem(`pending_room_creation_${quoteId}`);
    return data ? JSON.parse(data) : null;
  }, [quoteId]);

  const isButtonDisabled = React.useMemo(() => {
    if (isCreatingRoom) return true;
    
    if (pendingRoomData && userAndRoomStatus !== 'fulfilledUserData') {
      return true;
    }
    
    return false;
  }, [isCreatingRoom, pendingRoomData, userAndRoomStatus]);

  React.useEffect(() => {
    return () => {
      setIsCreatingRoom(false);
      hasAttemptedCreationRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    const createRoomAfterPayment = async () => {
      const pendingRoomData = localStorage.getItem(`pending_room_creation_${quoteId}`);
      
      if (!pendingRoomData || !isOpen) return;
      
      if (userAndRoomStatus === 'pendingUserData') return;
      
      if (hasAttemptedCreationRef.current) return;
      hasAttemptedCreationRef.current = true;

      try {
        const roomData = JSON.parse(pendingRoomData);
        
        if (roomData.mode === 'update_room') {
          setIsCreatingRoom(true);
          await billing.changeQuote({ 
            room_id: roomData.room_id, 
            quote_id: quoteId as string 
          });
          
          messageApi?.success(t('Tariffs.flow_selection.transfer_success'));
          localStorage.removeItem(`pending_room_creation_${quoteId}`);
          localStorage.removeItem('ended_tariff_lifetime');
          setIsCreatingRoom(false);
          navigate(`/room/${roomData.room_id}/documents`);
          return;
        }

        if (roomData.mode === 'single') {
          setIsCreatingRoom(true);
          await dispatch(updateRooms());
          localStorage.removeItem(`pending_room_creation_${quoteId}`);
          setIsCreatingRoom(false);
          return;
        }
      } catch (err: unknown) {
        setIsCreatingRoom(false);
        hasAttemptedCreationRef.current = false; 
        localStorage.removeItem(`pending_room_creation_${quoteId}`);
        const details = err instanceof AxiosError && err?.response?.data?.error;

        switch (details) {
          case 'room already exists':
            messageApi?.warning(t('Header.error.alreadyExists'));
            break;
          default:
            messageApi?.error(t('Header.error.create'));
        }
      }
    };

    createRoomAfterPayment();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, quoteId, userAndRoomStatus]);

  return (
    <Modal
      open={isOpen}
      width={'60%'}
      onCancel={onCancel}
      onOk={onCancel}
      centered
      cancelButtonProps={{style: {display: 'none'}}}
      okButtonProps={{ disabled: isButtonDisabled }}
      confirmLoading={isCreatingRoom || isButtonDisabled}
      closable={false}
      mask={{ closable: false }}
      className={getTheme()}
    >
      <div className={classes.confirmWrap}>
        <CheckSquareOutlined className={classes.successIcon}/>
        <div className={classes.textConfirm}>
          <p>{t('payment.dear')} {userData?.first_name} {userData?.last_name}</p>
          <p>{t('payment.pleasedOffer')}</p>
          <p>{t('payment.available')}</p>
          <p>{t('payment.thanks')}</p>
        </div>
      </div>
    </Modal>
  );
};

export default SuccessfulPayment;
